import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Link } from '../../navigation/Link';
import { Colors, Typography } from '../../../theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { TabsDropdown } from './TabsDropdown';
const indicatorTrackHeight = 3;
const Styles = labelStyles('Tabs', {
    root: (size) => [
        css `
      ${Typography.paragraph3Semibold}
      position: relative;
      display: grid;
      grid-template-columns: 1fr auto;
      column-gap: 20px;
      row-gap: 2px;
      align-items: center;
    `,
        size === 'medium' &&
            withModifier('medium', css `
          height: 50px;
        `),
        size === 'large' &&
            withModifier('large', css `
          height: 70px;
        `),
    ],
    primary: ({ count }) => [
        css `
      display: grid;
      height: 100%;
      // Allow shrinking
      min-width: 0;
      max-width: 100%;
    `,
        withModifier(`count-${count}`, css `
        grid-template-columns: repeat(${count}, minmax(auto, min-content));
      `),
    ],
    item: ({ isSelected }) => [
        css `
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: ${Colors.neutral800};
      font-weight: 600;
      text-decoration: none;
      padding: 0 20px;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        color: ${Colors.blue};
        outline: none;
      }

      &[disabled] {
        color: ${Colors.neutral500};
        cursor: not-allowed;
      }
    `,
        isSelected &&
            withModifier('selected', css `
          color: ${Colors.blue};
        `),
    ],
    itemText: css `
    display: block;
    white-space: nowrap;
  `,
    indicatorTrack: (shouldRenderTrack) => [
        css `
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      border-radius: ${indicatorTrackHeight / 2}px;
      height: ${indicatorTrackHeight}px;
      overflow: hidden;
    `,
        shouldRenderTrack &&
            withModifier('visible', css `
          background-color: ${Colors.neutral300};
        `),
    ],
    indicator: css `
    position: absolute;
    height: inherit;
    border-radius: inherit;
    background-color: ${Colors.blue};
    transition: all 250ms ease-in-out;
  `,
});
const TabsItem = (props) => {
    if (props.disabled) {
        return (isolateComponent("button", { type: "button", "aria-label": props.ariaLabel, css: Styles.item(props), disabled: true },
            isolateComponent("span", { css: Styles.itemText }, props.text)));
    }
    return (isolateComponent(Link, { ariaLabel: props.ariaLabel, ariaCurrent: props.isSelected ? props.ariaCurrent : undefined, color: null, css: Styles.item(props), href: props.href, onClick: props.onChoose, ref: props.forwardedRef },
        isolateComponent("span", { css: Styles.itemText }, props.text)));
};
export const Tabs = ({ items, selectedItemHref, forwardedRef = undefined, ariaLabel = undefined, ariaCurrent = 'page', isItemSelected = (item, selectedItemHref) => item.href === selectedItemHref, onChooseItem = () => { }, shouldRenderTrack = true, size = 'large', }) => {
    const selectedItemRef = useRef(null);
    const [indicatorStyles, setIndicatorStyles] = useState({});
    const shouldRenderIndicator = !items.every((i) => i.isExtra);
    const primaryItems = items.filter((item) => !item.isExtra);
    const primaryLinks = primaryItems.map((item) => {
        const isSelected = isItemSelected(item, selectedItemHref);
        return (isolateComponent(TabsItem, Object.assign({}, item, { ariaCurrent: ariaCurrent, forwardedRef: isSelected && shouldRenderIndicator ? selectedItemRef : null, isSelected: isSelected, key: item.href, onChoose: (event) => onChooseItem(item, event) })));
    });
    const secondaryItems = items.filter((item) => item.isExtra);
    const isSecondaryItemSelected = secondaryItems.some((item) => {
        return isItemSelected(item, selectedItemHref);
    });
    const secondaryMenu = secondaryItems.length > 0 && (isolateComponent("div", { ref: isSecondaryItemSelected && shouldRenderIndicator
            ? selectedItemRef
            : null },
        isolateComponent(TabsDropdown, { size: size, items: secondaryItems, onChooseItem: onChooseItem, selectedItemHref: selectedItemHref })));
    useEffect(() => {
        const requestId = requestAnimationFrame(() => {
            setIndicatorStyles(selectedItemRef.current
                ? {
                    left: selectedItemRef.current.offsetLeft,
                    width: selectedItemRef.current.offsetWidth,
                }
                : {});
        });
        return () => cancelAnimationFrame(requestId);
    }, [items, selectedItemHref]);
    return (isolateComponent("nav", { "aria-label": ariaLabel, css: Styles.root(size) },
        isolateComponent("div", { css: Styles.primary({ count: primaryLinks.length }), ref: forwardedRef }, primaryLinks),
        secondaryMenu,
        isolateComponent("div", { css: Styles.indicatorTrack(shouldRenderTrack) },
            isolateComponent("div", { css: Styles.indicator, style: indicatorStyles }))));
};
//# sourceMappingURL=Tabs.js.map
// @zapier/design-system@6.14.1 (10126c41d)
